// store/ws.ts
import { useEffect, useRef, useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { createStoreProvider } from 'utils/context';
import { useMessage } from 'store/message';
import { MessageType, MessageTypeEnum } from 'types';
import { useCookies } from 'react-cookie';
import { config } from '../../config/config';

function hasAuthCookie(): boolean {
  return (
    document.cookie.includes('Authorization=')
    || document.cookie.includes('access_token=')
  );
}

function useWsState() {
  const [wsMessage, setWsMessage] = useState<MessageEvent<any>>();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const [cookies] = useCookies(['Authorization']);

  const { setMessage } = useMessage();
  const { isAuthenticated, isLoading } = useAuth0();

  const sendWSMessage = useCallback(
    (messageType: MessageType, payload: any) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            messageId: uuid(),
            messageType,
            payload,
          }),
        );
      } else {
        console.warn('WebSocket not open. Cannot send message.');
      }
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated || isLoading || wsRef.current || !hasAuthCookie()) return;

    const ocppVersion = localStorage.getItem('ocppVersion');
    const socketUrl = ocppVersion === 'ocpp2.0.1' ? config.wsEndpoint2_0 : config.wsEndpoint1_6;

    const socket = new WebSocket(socketUrl);
    wsRef.current = socket;

    socket.onopen = () => {
      setIsConnected(true);
      const pathname = window.location.pathname.split('/').reverse();
      const chargePointId = pathname[0] !== 'login'
        && (pathname[0] === 'logs' || pathname[0] === 'settings'
          ? pathname[1]
          : pathname[0]);

      if (chargePointId) {
        sendWSMessage(MessageTypeEnum.RESET_CLIENT_CONNECTION, {
          chargePointId,
        });
        sendWSMessage(MessageTypeEnum.GET_SETTINGS, { chargePointId });
      }
    };

    socket.onmessage = (event) => setWsMessage(event);

    socket.onerror = () => setMessage({ text: 'Something went wrong!', type: 'error' });

    socket.onclose = () => {
      wsRef.current = null;
      setMessage({ text: 'WebSocket Connection closed!', type: 'info' });
      setIsConnected(false);
    };
  }, [isAuthenticated, isLoading, sendWSMessage, setMessage, cookies.Authorization]);

  return {
    ws: wsRef.current,
    wsMessage,
    isConnected,
    sendWSMessage,
  };
}

export const [useWs, WsProvider] = createStoreProvider(useWsState);
